// ============================================================================
// SOP list view — sortable table alternative to the Kanban. Columns per spec:
// Account, Facility, Market, Stage, Priority, Start Date, Days in Stage,
// Monthly $, Status. All sortable. Default sort: stage (canonical order),
// then created_date desc.
//
// Mobile: the table scrolls horizontally inside its container — preferable
// to a card-stack rebuild because the AM use case for "list view" is
// scanning many rows at once. The Kanban view already covers the
// stack-friendly mobile layout.
// ============================================================================

import { useMemo, useState } from 'react';
import {
  NEW_ACCOUNT_SOP_STAGES,
  NEW_ACCOUNT_SOP_STAGE_LABELS,
  type NewAccountSop,
} from '../../shared/types';
import { parseStartDate } from './lib/lifecycle';
import { feeChip, usablesChip, type IntakeChip } from './intake-answers';
import { useMarkets, marketColorPair } from '../../shared/markets';
import { hourlyCardRates, perHourShort, rateBasisChip } from '../../shared/rate-basis';

type SortKey =
  | 'account'
  | 'facility'
  | 'market'
  | 'stage'
  | 'priority'
  | 'start_date'
  | 'days_in_stage'
  | 'sub_payment'
  | 'final_pricing'
  | 'management_fee_pct'
  | 'tcs_provides_usables'
  | 'status';

interface Props {
  rows: NewAccountSop[];
  onSelect: (row: NewAccountSop) => void;
}

const STAGE_ORDER = new Map<string, number>(
  NEW_ACCOUNT_SOP_STAGES.map((s, i) => [s, i]),
);

export default function SopList({ rows, onSelect }: Props) {
  const [sortKey, setSortKey] = useState<SortKey>('stage');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  // The market chip reads the market's own row, not the card's stored copy.
  const { markets } = useMarkets();

  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      // Items 831/758: on the money columns an hourly card's per-hour figure
      // is not a monthly amount, so it always sorts after the monthly rows,
      // whichever way the column is sorted.
      if (sortKey === 'final_pricing' || sortKey === 'sub_payment') {
        const ha = hourlyCardRates(a.service_lines) != null;
        const hb = hourlyCardRates(b.service_lines) != null;
        if (ha !== hb) return ha ? 1 : -1;
      }
      const va = sortValue(a, sortKey);
      const vb = sortValue(b, sortKey);
      const cmp = compare(va, vb);
      if (cmp !== 0) return sortDir === 'asc' ? cmp : -cmp;
      // Stable secondary sort: created_date desc, then sop_id asc.
      const da = a.created_date ?? '';
      const db = b.created_date ?? '';
      if (da !== db) return da > db ? -1 : 1;
      return a.sop_id.localeCompare(b.sop_id);
    });
    return copy;
  }, [rows, sortKey, sortDir]);

  function toggleSort(k: SortKey) {
    if (k === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(k);
      // Most fields read more naturally ascending except numeric ones.
      setSortDir(
        k === 'days_in_stage' || k === 'sub_payment' ||
        k === 'final_pricing' || k === 'start_date'
          ? 'desc'
          : 'asc',
      );
    }
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-tcs-border bg-tcs-raised">
      <table className="min-w-full text-sm">
        <thead className="bg-tcs-bg-muted text-xs uppercase tracking-wide text-tcs-text-muted">
          <tr>
            <Th sortKey="account" label="Account" current={sortKey} dir={sortDir} onClick={toggleSort} />
            <Th sortKey="facility" label="Facility" current={sortKey} dir={sortDir} onClick={toggleSort} />
            <Th sortKey="market" label="Market" current={sortKey} dir={sortDir} onClick={toggleSort} />
            <Th sortKey="stage" label="Stage" current={sortKey} dir={sortDir} onClick={toggleSort} />
            <Th sortKey="priority" label="Priority" current={sortKey} dir={sortDir} onClick={toggleSort} />
            <Th sortKey="start_date" label="Start" current={sortKey} dir={sortDir} onClick={toggleSort} />
            <Th sortKey="days_in_stage" label="Days" current={sortKey} dir={sortDir} onClick={toggleSort} numeric />
            {/* AFB-0070: the board showed only what we PAY the sub and never
                what the client PAYS US, so margin was invisible from the list.
                final_pricing was already on the row type — only the column was
                missing. */}
            <Th sortKey="final_pricing" label="Client $/mo" current={sortKey} dir={sortDir} onClick={toggleSort} numeric />
            <Th sortKey="sub_payment" label="Sub $/mo" current={sortKey} dir={sortDir} onClick={toggleSort} numeric />
            {/* AFB-0081: both were captured at intake and shown back nowhere. */}
            <Th sortKey="management_fee_pct" label="Fee %" current={sortKey} dir={sortDir} onClick={toggleSort} numeric />
            <Th sortKey="tcs_provides_usables" label="Usables" current={sortKey} dir={sortDir} onClick={toggleSort} />
            <Th sortKey="status" label="Status" current={sortKey} dir={sortDir} onClick={toggleSort} />
          </tr>
        </thead>
        <tbody className="divide-y divide-tcs-border">
          {sorted.length === 0 ? (
            <tr>
              <td colSpan={12} className="px-3 py-8 text-center text-xs text-tcs-text-muted">
                No SOPs match the current filters.
              </td>
            </tr>
          ) : (
            sorted.map((r) => {
              const stalled = r.status === 'Stalled' || r.stage === 'Stalled';
              const hourly = hourlyCardRates(r.service_lines);
              return (
                <tr
                  key={r.sop_id}
                  onClick={() => onSelect(r)}
                  className={`cursor-pointer hover:bg-tcs-bg-muted ${stalled ? 'bg-st-danger-bg/50' : ''}`}
                >
                  <td className="px-3 py-2 text-tcs-text">
                    <div className="font-medium">
                      {r.account_name?.trim() || r.sop_id}
                    </div>
                    <div className="text-[11px] text-tcs-text-muted">{r.sop_id}</div>
                  </td>
                  <td className="px-3 py-2 text-tcs-text-secondary">
                    {r.facility_name?.trim() || '—'}
                  </td>
                  <td className="px-3 py-2">
                    {r.market_region ? (
                      <span
                        className="inline-block rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide"
                        style={{
                          backgroundColor: 'var(--tcs-bg-muted)',
                          color: marketColorPair(markets, r.market_region).base,
                        }}
                      >
                        {r.market_region}
                      </span>
                    ) : (
                      <span className="text-tcs-text-muted">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-tcs-text-secondary">
                    {stageLabel(r.stage)}
                  </td>
                  <td className="px-3 py-2 text-tcs-text-secondary">
                    {r.priority ?? <span className="text-tcs-text-muted">—</span>}
                  </td>
                  <td className="px-3 py-2 text-tcs-text-secondary">
                    {r.start_date ? formatDate(r.start_date) : <span className="text-tcs-text-muted">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right text-tcs-text-secondary tabular-nums">
                    {r.days_in_stage ?? <span className="text-tcs-text-muted">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right text-tcs-text-secondary tabular-nums">
                    {hourly ? (
                      <HourlyCell rate={hourly.client} />
                    ) : typeof r.final_pricing === 'number'
                      ? `$${formatMoney(r.final_pricing)}`
                      : <span className="text-tcs-text-muted">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right text-tcs-text-secondary tabular-nums">
                    {hourly ? (
                      <HourlyCell rate={hourly.sub} />
                    ) : typeof r.sub_payment === 'number'
                      ? `$${formatMoney(r.sub_payment)}`
                      : <span className="text-tcs-text-muted">—</span>}
                  </td>
                  {/* AFB-0081 — sortable, because "which cards still owe me an
                      answer?" is the question Jose was actually asking and a
                      sort answers it in one click. Missing answers sort last
                      ascending (see sortValue), so sorting descending puts them
                      at the top. */}
                  <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                    <IntakeCell chip={feeChip(r)} />
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">
                    <IntakeCell chip={usablesChip(r)} />
                  </td>
                  <td className="px-3 py-2">
                    <StatusPill value={r.status} />
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Helpers ----------

function Th({
  sortKey,
  label,
  current,
  dir,
  onClick,
  numeric,
}: {
  sortKey: SortKey;
  label: string;
  current: SortKey;
  dir: 'asc' | 'desc';
  onClick: (k: SortKey) => void;
  numeric?: boolean;
}) {
  const active = current === sortKey;
  return (
    <th
      scope="col"
      className={`whitespace-nowrap px-3 py-2 font-medium ${numeric ? 'text-right' : 'text-left'}`}
    >
      <button
        type="button"
        onClick={() => onClick(sortKey)}
        className={`inline-flex items-center gap-1 ${active ? 'text-tcs-text' : 'text-tcs-text-muted'} hover:text-tcs-text`}
      >
        {label}
        {active && (
          <span aria-hidden className="text-[9px]">
            {dir === 'asc' ? '▲' : '▼'}
          </span>
        )}
      </button>
    </th>
  );
}

// AFB-0081. The table equivalent of the Kanban chip. An answered value renders
// as plain text — a table of highlighted cells highlights nothing — and only a
// gap that somebody can still close is tinted.
function IntakeCell({ chip }: { chip: IntakeChip }) {
  if (chip.tone === 'answered') {
    return (
      <span className="text-tcs-text-secondary" title={chip.title}>
        {chip.text.replace(/^(Fee |Usables: )/, '')}
      </span>
    );
  }
  if (chip.tone === 'muted') {
    return (
      <span className="text-[11px] italic text-tcs-text-muted" title={chip.title}>
        not answered
      </span>
    );
  }
  return (
    <span
      className="rounded bg-st-warn-bg-strong px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-st-warn-fg-strong"
      title={chip.title}
    >
      Not answered
    </span>
  );
}

function StatusPill({ value }: { value: string | null }) {
  if (!value) return <span className="text-tcs-text-muted">—</span>;
  if (value === 'Stalled') {
    return (
      <span className="rounded bg-st-danger-bg-strong px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-st-danger-fg-strong">
        Stalled
      </span>
    );
  }
  if (value === 'Active') {
    return (
      <span className="rounded bg-st-ok-bg-strong px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-st-ok-fg-strong">
        Active
      </span>
    );
  }
  return (
    <span className="rounded bg-tcs-surface px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-tcs-text-secondary">
      {value}
    </span>
  );
}

function stageLabel(s: string | null): string {
  if (!s) return '—';
  if ((NEW_ACCOUNT_SOP_STAGE_LABELS as Record<string, string>)[s]) {
    return (NEW_ACCOUNT_SOP_STAGE_LABELS as Record<string, string>)[s];
  }
  return s;
}

/** "Hourly" with the per-hour figure underneath (items 831/758). Never a monthly amount. */
function HourlyCell({ rate }: { rate: number | null }) {
  return (
    <span className="inline-flex flex-col items-end" data-field="list_hourly">
      <span className="text-[11px] font-semibold text-tcs-text-secondary">{rateBasisChip('hourly')}</span>
      <span className="text-[11px] text-tcs-text-muted">{rate != null ? perHourShort(rate) : '—'}</span>
    </span>
  );
}

function sortValue(r: NewAccountSop, k: SortKey): string | number {
  switch (k) {
    case 'account':
      return (r.account_name ?? '').toLowerCase();
    case 'facility':
      return (r.facility_name ?? '').toLowerCase();
    case 'market':
      return (r.market_region ?? '').toLowerCase();
    case 'stage':
      // Canonical stage order — unknown stages sort to the end.
      return STAGE_ORDER.get(r.stage as string) ?? Number.MAX_SAFE_INTEGER;
    case 'priority': {
      const p = (r.priority ?? '').toLowerCase();
      // High → Medium → Low (standard triage ordering).
      if (p === 'high') return 0;
      if (p === 'medium') return 1;
      if (p === 'low') return 2;
      return 3;
    }
    case 'start_date':
      return r.start_date ?? '';
    case 'days_in_stage':
      return r.days_in_stage ?? -1;
    case 'sub_payment':
      return r.sub_payment ?? -1;
    case 'final_pricing':
      return r.final_pricing ?? -1;
    case 'status':
      return (r.status ?? '').toLowerCase();
    // AFB-0081. NULL sorts to the END ascending — the opposite of the -1 idiom
    // used for money above — because for these two columns NULL is the value
    // somebody has to act on, and "sort descending to see the gaps" is one
    // click. A deliberate 0% fee is 0 and sorts with the other real answers,
    // which is the whole distinction the column exists to show.
    case 'management_fee_pct':
      return r.management_fee_pct ?? Number.MAX_SAFE_INTEGER;
    case 'tcs_provides_usables':
      // TCS (0) → client (1) → unanswered (last).
      if (r.tcs_provides_usables === true) return 0;
      if (r.tcs_provides_usables === false) return 1;
      return Number.MAX_SAFE_INTEGER;
  }
}

function compare(a: string | number, b: string | number): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  const sa = String(a);
  const sb = String(b);
  if (sa === sb) return 0;
  return sa < sb ? -1 : 1;
}

/**
 * AFB-0070, fixed 2026-08-07 (CW-G Phase 4c).
 *
 * This used to call `Date.parse(iso)` directly. For a DATE-ONLY string like
 * "2026-08-01", ECMA-262 requires that be parsed as **UTC midnight**;
 * `toLocaleDateString()` then renders it in America/Chicago (UTC−5) and prints
 * **Jul 31**. Exactly the "a day prior" Jose reported.
 *
 * Routing this through `parseStartDate()` in `lib/lifecycle.ts`, which builds a
 * LOCAL Date from the Y/M/D components, makes every view share one definition of
 * "the day this account starts" instead of several.
 *
 * ⛔ This comment used to say "the Kanban card was always right" for exactly
 * that reason. It was not. `buildStartInfo` in sop-card.tsx had its own
 * `new Date(iso)` and printed every board date a day early until 2026-09-17,
 * while the status pill beside it — which does call `parseStartDate` — printed
 * the right one. **A claim about a screen nobody opened is not a measurement.**
 */
export function formatDate(iso: string): string {
  const d = parseStartDate(iso);
  if (!d) return iso;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatMoney(n: number): string {
  return Math.round(n).toLocaleString();
}
